import { useEffect, useState, useMemo } from "react";
import Link from "next/link";
import { format } from "date-fns";
import { Card, CardContent } from "@/components/ui/card";
import { Calendar, ChevronRight } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";

// Image cache for destinations across component instances
const imageCache = new Map<string, string>();

interface TripCardProps {
  trip: {
    id: string;
    destination: string;
    startDate: string;
    endDate: string;
  };
}

const TripCard = ({ trip }: TripCardProps) => {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [imageLoading, setImageLoading] = useState(true);
  
  // Create a memoized unique key for this destination
  const cacheKey = useMemo(() => trip.destination, [trip.destination]);

  useEffect(() => {
    // Use the cached image if available
    if (imageCache.has(cacheKey)) {
      setImageUrl(imageCache.get(cacheKey) || null);
      setImageLoading(false);
      return;
    }
    
    // Abort controller for cleanup
    const controller = new AbortController();
    
    const fetchImage = async () => {
      try {
        // Add signal to fetch for cancellation support
        const response = await fetch(
          `/api/proxy/images?q=${encodeURIComponent(trip.destination + ' landmark')}`,
          { signal: controller.signal }
        );
        
        if (!response.ok) throw new Error("Failed to fetch image");
        
        const data = await response.json();
        const newImageUrl = data.results[0]?.url;
        
        if (newImageUrl) {
          // Cache the result
          imageCache.set(cacheKey, newImageUrl);
          setImageUrl(newImageUrl);
        }
      } catch (error) {
        // Ignore AbortError
        if (error instanceof Error && error.name !== 'AbortError') {
          console.error("Error fetching image:", error);
        }
      } finally {
        if (!controller.signal.aborted) {
          setImageLoading(false);
        }
      }
    };

    fetchImage();
    
    // Cleanup function to abort fetch on unmount
    return () => {
      controller.abort();
    };
  }, [cacheKey]);

  return (
    <Link href={`/trips/${trip.id}`}>
      <Card className="group hover:shadow-lg transition-all duration-200 border-gray-200 hover:border-indigo-200 overflow-hidden relative h-32">
        {imageLoading ? (
          <Skeleton className="absolute inset-0 bg-gradient-to-br from-indigo-600/60 to-purple-600/60" />
        ) : (
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: imageUrl ? `url(${imageUrl})` : "none",
            }}
          >
            <div className="absolute inset-0 bg-black/40 group-hover:bg-black/50 transition-colors" />
          </div>
        )}
        
        <CardContent className="p-6 relative z-10 h-full flex flex-col justify-between">
          <div className="space-y-2">
            <h2 className="text-2xl font-semibold text-white group-hover:text-white/90 transition-colors">
              {trip.destination}
            </h2>
            <div className="flex items-center text-white/80">
              <Calendar className="w-4 h-4 mr-2 flex-shrink-0" />
              <span>
                {format(new Date(trip.startDate), "MMM d")} -{" "}
                {format(new Date(trip.endDate), "MMM d, yyyy")}
              </span>
            </div>
          </div>
          
          <div className="flex justify-end">
            <ChevronRight className="w-5 h-5 text-white/80 group-hover:text-white transition-colors" />
          </div>
        </CardContent>
      </Card>
    </Link>
  );
};

export default TripCard;